import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, LogOut, User, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/notifications");
        setUnread(data.filter((n) => !n.read).length);
      } catch (err) {
        logger.warn("Failed to load notifications:", err?.response?.status || err?.message);
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 15000);
    return () => clearInterval(id);
  }, [user]);

  const investorLinks = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/properties", label: "Properties" },
    { to: "/my-tokens", label: "My Tokens" },
    { to: "/marketplace", label: "Marketplace" },
    { to: "/referrals", label: "Refer" },
    { to: "/transactions", label: "Transactions" },
  ];

  const adminLinks = [
    { to: "/admin", label: "Analytics" },
    { to: "/admin/properties", label: "Properties" },
    { to: "/admin/tokens", label: "Tokens" },
    { to: "/admin/investors", label: "Investors" },
    { to: "/admin/kyc", label: "KYC" },
    { to: "/admin/bank-transfers", label: "Transfers" },
    { to: "/admin/payment-settings", label: "Settings" },
  ];

  const links = user?.role === "admin" ? adminLinks : user ? investorLinks : [];

  return (
    <div className="min-h-screen bg-[#050914]">
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to={user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/"} className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#FDE047] flex items-center justify-center">
              <span className="font-heading font-bold text-[#050914] text-sm">BP</span>
            </div>
            <div className="font-heading text-[15px] tracking-tight leading-none">
              <div className="text-white">BLOCKCHAIN</div>
              <div className="text-[#D4AF37] text-[11px] tracking-[0.2em]">PROPERTIES</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end
                data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm rounded-full transition-colors ${
                    isActive
                      ? "text-[#D4AF37] bg-[#D4AF37]/10"
                      : "text-gray-400 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <button
                  onClick={() => navigate("/notifications")}
                  data-testid="nav-notifications-bell"
                  className="relative w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
                >
                  <Bell className="w-4 h-4 text-gray-300" />
                  {unread > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#D4AF37] text-[#050914] text-[10px] font-bold flex items-center justify-center">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-user-menu"
                      className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#FDE047] flex items-center justify-center font-heading text-sm text-[#050914] font-bold"
                    >
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#0B1120] border-white/10 text-white">
                    <div className="px-2 py-1.5">
                      <div className="text-sm font-medium">{user.name}</div>
                      <div className="text-xs text-gray-400">{user.email}</div>
                      <div className="text-xs text-[#D4AF37] mt-1 uppercase tracking-wider">{user.role}</div>
                    </div>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")} data-testid="menu-dashboard">
                      <User className="w-4 h-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <button
                  className="md:hidden w-9 h-9 rounded-full bg-white/5 flex items-center justify-center"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  data-testid="nav-mobile-toggle"
                >
                  {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                </button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="text-gray-300 hover:text-white"
                  onClick={() => navigate("/login")}
                  data-testid="nav-login-btn"
                >
                  Log in
                </Button>
                <Button
                  className="btn-gold rounded-full"
                  onClick={() => navigate("/register")}
                  data-testid="nav-register-btn"
                >
                  Get started
                </Button>
              </>
            )}
          </div>
        </div>

        {mobileOpen && user && (
          <div className="md:hidden border-t border-white/10 bg-[#0B1120]">
            <div className="px-4 py-2 flex flex-col">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `py-3 text-sm ${isActive ? "text-[#D4AF37]" : "text-gray-300"}`
                  }
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="min-h-[calc(100vh-4rem)]">{children}</main>
    </div>
  );
}
